import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
    FilePlus, Loader2, RotateCcw, Send,
    Plus, Trash2, Table2, FileStack, CheckCircle, Navigation, Paperclip, Building2,
    Upload, Download,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';
import { inputCls, Label, SelectIcon, SectionHeader, InnerHeader } from '../../components/vendor/sharedVendorUI';
import { formatIndianCurrency, getAmountDisplay } from '../../utilities/amountToTextHelper';
import { uploadFileToS3 } from '../../api/commonAPI/awsUploadAPI';
import { S3_FOLDERS } from '../../config/s3Config';

import {
    fetchVendorsForSPPO, fetchBudgetCCForSPPO, fetchDCAByCCForSPPO, fetchSubDCAbyDCA,
    fetchUpdloadDocSPPOValue, saveNewServiceProvider,
    clearDCAList, clearSubDCAList, clearSaveResult, resetSPPOCreation,
    selectVendorsArray, selectVendorsLoading,
    selectCostCentersArray, selectCostCentersLoading,
    selectDCAListArray, selectDCAListLoading,
    selectSubDCAListArray, selectSubDCAListLoading,
    selectUploadValue,
    selectSaveStatus, selectSaveLoading, selectSaveError,
} from '../../slices/spPOSlice/sppoCreationSlice';

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDateForAPI = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && /^\d{2}-[A-Za-z]{3}-\d{4}$/.test(val)) return val;
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const numOr0 = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };

const newItemRow = () => ({
    id: Date.now() + Math.random(),
    description: '', unit: '', quantity: '', ourRate: '', prwRate: '', rate: '',
});

const newTermRow = () => ({ id: Date.now() + Math.random(), term: '' });

// Expected Excel columns for bulk item import — matches the legacy template's headers.
const EXCEL_COLUMNS = ['Description', 'Unit', 'Quantity', 'OurRate', 'ApprovedPRWRate', 'Rate'];

const isRowBlank = (r) => !r.description && !r.unit && !r.quantity && !r.ourRate && !r.prwRate && !r.rate;

const parseExcelRows = (rows) => {
    const errors = [];
    const parsed = rows.map((row, idx) => {
        const rowNum = idx + 2; // +1 for header row, +1 for 1-based
        const description = String(row.Description ?? '').trim();
        const unit = String(row.Unit ?? '').trim();
        const quantity = row.Quantity;
        const ourRate = row.OurRate;
        const prwRate = row.ApprovedPRWRate;
        const rate = row.Rate;

        if (!description) errors.push(`Row ${rowNum}: Description is required.`);
        if (!unit) errors.push(`Row ${rowNum}: Unit is required.`);
        if (quantity === '' || quantity === undefined || isNaN(Number(quantity)) || Number(quantity) <= 0) errors.push(`Row ${rowNum}: Quantity must be a positive number.`);
        if (ourRate === '' || ourRate === undefined || isNaN(Number(ourRate)) || Number(ourRate) <= 0) errors.push(`Row ${rowNum}: OurRate must be a positive number.`);
        if (prwRate === '' || prwRate === undefined || isNaN(Number(prwRate)) || Number(prwRate) <= 0) errors.push(`Row ${rowNum}: ApprovedPRWRate must be a positive number.`);
        if (rate === '' || rate === undefined || isNaN(Number(rate)) || Number(rate) <= 0) errors.push(`Row ${rowNum}: Rate must be a positive number.`);
        if (!isNaN(Number(prwRate)) && !isNaN(Number(rate)) && Number(prwRate) < Number(rate)) {
            errors.push(`Row ${rowNum}: Rate cannot exceed ApprovedPRWRate.`);
        }

        return {
            id: Date.now() + Math.random() + idx,
            description,
            unit,
            quantity: quantity === undefined ? '' : String(quantity),
            ourRate: ourRate === undefined ? '' : String(ourRate),
            prwRate: prwRate === undefined ? '' : String(prwRate),
            rate: rate === undefined ? '' : String(rate),
        };
    });
    return { parsed, errors };
};

const SPPOCreation = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || 0;
    const userName = userData?.userName || userData?.UserName || 'system';

    const vendors = useSelector(selectVendorsArray);
    const vendorsLoading = useSelector(selectVendorsLoading);
    const costCenters = useSelector(selectCostCentersArray);
    const ccLoading = useSelector(selectCostCentersLoading);
    const dcaList = useSelector(selectDCAListArray);
    const dcaLoading = useSelector(selectDCAListLoading);
    const subDcaList = useSelector(selectSubDCAListArray);
    const subDcaLoading = useSelector(selectSubDCAListLoading);
    const uploadValue = useSelector(selectUploadValue);
    const saveStatus = useSelector(selectSaveStatus);
    const saveLoading = useSelector(selectSaveLoading);
    const saveError = useSelector(selectSaveError);

    const [vendorCode, setVendorCode] = useState('');
    const [ccCode, setCcCode] = useState('');
    const [dcaCode, setDcaCode] = useState('');
    const [subDcaCode, setSubDcaCode] = useState('');
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());

    const [items, setItems] = useState([newItemRow()]);
    const [terms, setTerms] = useState([newTermRow()]);
    const [remarks, setRemarks] = useState('');
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const excelInputRef = useRef(null);

    useEffect(() => {
        dispatch(fetchVendorsForSPPO());
        dispatch(fetchBudgetCCForSPPO());
        return () => { dispatch(resetSPPOCreation()); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        setDcaCode(''); setSubDcaCode('');
        dispatch(clearDCAList()); dispatch(clearSubDCAList());
        if (ccCode) {
            dispatch(fetchDCAByCCForSPPO(ccCode));
            dispatch(fetchUpdloadDocSPPOValue(ccCode));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ccCode]);

    useEffect(() => {
        setSubDcaCode('');
        dispatch(clearSubDCAList());
        if (dcaCode) dispatch(fetchSubDCAbyDCA(dcaCode));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dcaCode]);

    useEffect(() => {
        if (saveStatus === 'failed' && saveError) { toast.error(saveError); dispatch(clearSaveResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveStatus]);

    const updateItem = (id, patch) => setItems((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    const itemAmount = (row) => (row.quantity && row.rate ? numOr0(row.quantity) * numOr0(row.rate) : 0);
    const updateTerm = (id, term) => setTerms((prev) => prev.map((t) => (t.id === id ? { ...t, term } : t)));

    // ── Excel bulk item import ────────────────────────────────────────────
    const handleExcelFile = (selectedFile) => {
        if (!selectedFile) return;
        if (!selectedFile.name.match(/\.(xlsx|xls)$/i)) {
            toast.error('Please upload a valid Excel file (.xlsx or .xls)');
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

                if (rows.length === 0) {
                    toast.warn('The uploaded file has no data rows.');
                    return;
                }

                const { parsed, errors } = parseExcelRows(rows);
                if (errors.length > 0) {
                    toast.error(`Excel import failed (${errors.length} issue${errors.length > 1 ? 's' : ''}). First: ${errors[0]}`);
                    return;
                }

                setItems((prev) => (prev.length === 1 && isRowBlank(prev[0]) ? parsed : [...prev, ...parsed]));
                toast.success(`Imported ${parsed.length} item${parsed.length !== 1 ? 's' : ''} from Excel.`);
            } catch (err) {
                toast.error('Failed to parse Excel file: ' + err.message);
            }
        };
        reader.readAsArrayBuffer(selectedFile);
    };

    const handleExcelInputChange = (e) => {
        const f = e.target.files?.[0];
        if (f) handleExcelFile(f);
        e.target.value = '';
    };

    const handleDownloadTemplate = () => {
        const sample = ['Sample item description', 'Nos', 10, 100, 120, 110];
        const ws = XLSX.utils.aoa_to_sheet([EXCEL_COLUMNS, sample]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Items');
        XLSX.writeFile(wb, 'SPPO_Item_Template.xlsx');
    };

    const gridTotal = useMemo(() => items.reduce((s, r) => s + itemAmount(r), 0), [items]);
    const requiresUpload = gridTotal > uploadValue;

    const handleFullReset = () => {
        setVendorCode(''); setCcCode(''); setDcaCode(''); setSubDcaCode('');
        setStartDate(new Date()); setEndDate(new Date());
        setItems([newItemRow()]); setTerms([newTermRow()]);
        setRemarks(''); setFile(null);
        if (excelInputRef.current) excelInputRef.current.value = '';
        dispatch(resetSPPOCreation());
        dispatch(fetchVendorsForSPPO());
        dispatch(fetchBudgetCCForSPPO());
    };

    const handleFileChange = (e) => {
        const f = e.target.files?.[0] || null;
        if (f && !f.name.toLowerCase().endsWith('.pdf')) { e.target.value = ''; setFile(null); return; }
        setFile(f);
    };

    const handleSubmit = async () => {
        if (!vendorCode) { toast.warn('Select Vendor.'); return; }
        if (!ccCode) { toast.warn('Select Cost Center.'); return; }
        if (!dcaCode) { toast.warn('Select Account Head.'); return; }
        if (!subDcaCode) { toast.warn('Select Sub Account Head.'); return; }
        if (!startDate) { toast.warn('Select PO Start Date.'); return; }
        if (!endDate) { toast.warn('Select PO Completion Date.'); return; }
        if (!remarks.trim()) { toast.warn('Enter Remarks.'); return; }

        for (const r of items) {
            if (!r.description.trim()) { toast.warn('Enter Description for every item.'); return; }
            if (!r.unit.trim()) { toast.warn('Enter Unit for every item.'); return; }
            if (!r.quantity || numOr0(r.quantity) <= 0) { toast.warn('Enter Quantity for every item.'); return; }
            if (!r.ourRate || numOr0(r.ourRate) <= 0) { toast.warn('Enter Our Rate for every item.'); return; }
            if (!r.prwRate || numOr0(r.prwRate) <= 0) { toast.warn('Enter Approved PRW Rate for every item.'); return; }
            if (!r.rate || numOr0(r.rate) <= 0) { toast.warn('Enter Rate for every item.'); return; }
            if (numOr0(r.prwRate) < numOr0(r.rate)) { toast.warn('Rate is greater than Approved PRW Rate.'); return; }
        }

        const validTerms = terms.filter((t) => t.term.trim());
        if (validTerms.length === 0) { toast.warn('Enter Atleast One Terms & Conditions.'); return; }

        if (requiresUpload && !file) { toast.warn('Upload the Final Scope Of Checklist document (required for this PO value).'); return; }

        const itemDescs = items.map((r) => r.description.replace(/\|/g, '')).join('|') + '|';
        const itemUnits = items.map((r) => r.unit).join('|') + '|';
        const itemQtys = items.map((r) => r.quantity).join('|') + '|';
        const itemClientRates = items.map((r) => r.ourRate).join('|') + '|';
        const itemPWRRate = items.map((r) => r.prwRate).join('|') + '|';
        const itemRates = items.map((r) => r.rate).join('|') + '|';
        const itemAmounts = items.map((r) => itemAmount(r).toFixed(2)).join('|') + '|';
        const termsJoined = validTerms.map((t) => t.term.trim().replace(/\|/g, '')).join('|') + '|';

        const extension = file ? file.name.split('.').pop() : '';

        const payload = {
            VendorCode: vendorCode,
            CCCode: ccCode,
            DCACode: dcaCode,
            SubDcaCode: subDcaCode,
            SPPOStartDate: formatDateForAPI(startDate),
            SPPOEndDate: formatDateForAPI(endDate),
            TotalValue: gridTotal,
            ItemDescs: itemDescs,
            ItemUnits: itemUnits,
            ItemQtys: itemQtys,
            ItemClientRates: itemClientRates,
            ItemPWRRate: itemPWRRate,
            ItemRates: itemRates,
            ItemAmounts: itemAmounts,
            Remarks: termsJoined,
            NewRemarks: remarks,
            Action: 'Add',
            PredefinedTermsExist: 'No',
            ItemTermHeadID: 0,
            PreferredRemarks: '',
            Filechk: file ? 'Yes' : 'No',
            Extension: extension,
            RoleId: roleId,
            CreatedBy: userName,
        };

        const result = await dispatch(saveNewServiceProvider(payload));
        if (saveNewServiceProvider.fulfilled.match(result)) {
            const responseStr = typeof result.payload?.Data === 'string' ? result.payload.Data : '';
            const [status, iCode] = responseStr.split('$');
            if (status === 'Submited') {
                if (file && iCode) {
                    setUploading(true);
                    try { await uploadFileToS3(file, S3_FOLDERS.SPPO, iCode); }
                    catch { toast.warn('PO saved, but the document upload failed. Please retry the upload separately.'); }
                    finally { setUploading(false); }
                }
                toast.success('Service Provider Details Added Successfully.');
                dispatch(clearSaveResult());
                handleFullReset();
            } else {
                // Legacy shows the SP's message when the status is not 'Submited'.
                toast.error(responseStr || 'Error Occurred While Adding New Service Provider');
            }
        }
    };

    const isBusy = saveLoading || uploading;
    const showSummary = terms.some((t) => t.term.trim()) && remarks.trim();
    const canSubmit = !isBusy;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            <div className="max-w-7xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-7 text-white">
                    <div className="absolute inset-0 opacity-10"
                        style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
                    <div className="absolute top-0 right-0 w-72 h-72 bg-purple-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                    <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20">
                                <FilePlus className="h-7 w-7 text-white" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">Purchase Module</span>
                                </div>
                                <h1 className="text-2xl md:text-3xl font-black tracking-tight">
                                    {menuData?.name || 'SPPO Creation'}
                                </h1>
                                <p className="text-indigo-200 text-sm mt-0.5">Create a new Service Provider Purchase Order</p>
                            </div>
                        </div>
                        <div className="hidden sm:flex items-center gap-3 text-indigo-200">
                            <div className="text-right">
                                <p className="text-xs uppercase tracking-wider">Module</p>
                                <p className="text-sm font-bold text-white">Purchase / SPPO Creation</p>
                            </div>
                            <Navigation className="h-5 w-5 opacity-60" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto space-y-6">

                {/* ── Section 1: PO Header ──────────────────────────────── */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <SectionHeader icon={Building2} title="PO Header" subtitle="Vendor, cost center and account head"
                        action={
                            <button onClick={handleFullReset} disabled={isBusy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50">
                                <RotateCcw className="h-3.5 w-3.5" /> Reset
                            </button>
                        }
                    />
                    <div className="p-6 md:p-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                            <div>
                                <Label required>Vendor</Label>
                                <div className="relative">
                                    <select className={`${inputCls} appearance-none pr-10`} value={vendorCode} onChange={(e) => setVendorCode(e.target.value)} disabled={vendorsLoading || isBusy}>
                                        <option value="">{vendorsLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {vendors.map((v) => <option key={v.VendorCode} value={v.VendorCode}>{v.VendorName}</option>)}
                                    </select>
                                    <SelectIcon loading={vendorsLoading} />
                                </div>
                            </div>
                            <div>
                                <Label required>Cost Center</Label>
                                <div className="relative">
                                    <select className={`${inputCls} appearance-none pr-10`} value={ccCode} onChange={(e) => setCcCode(e.target.value)} disabled={ccLoading || isBusy}>
                                        <option value="">{ccLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {costCenters.map((c) => <option key={c.CC_Code} value={c.CC_Code}>{c.CC_Code} — {c.CC_Name}</option>)}
                                    </select>
                                    <SelectIcon loading={ccLoading} />
                                </div>
                            </div>
                            <div>
                                <Label required>Account Head</Label>
                                <div className="relative">
                                    <select className={`${inputCls} appearance-none pr-10`} value={dcaCode} onChange={(e) => setDcaCode(e.target.value)} disabled={!ccCode || dcaLoading || isBusy}>
                                        <option value="">{!ccCode ? 'Select cost center first' : dcaLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {dcaList.map((d) => <option key={d.DCACode} value={d.DCACode}>{d.DCAIDSTR}</option>)}
                                    </select>
                                    <SelectIcon loading={dcaLoading} />
                                </div>
                            </div>
                            <div>
                                <Label required>Sub Account Head</Label>
                                <div className="relative">
                                    <select className={`${inputCls} appearance-none pr-10`} value={subDcaCode} onChange={(e) => setSubDcaCode(e.target.value)} disabled={!dcaCode || subDcaLoading || isBusy}>
                                        <option value="">{!dcaCode ? 'Select account head first' : subDcaLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {subDcaList.map((s) => <option key={s.SubDCACode} value={s.SubDCACode}>{s.SubDCAIDSTR}</option>)}
                                    </select>
                                    <SelectIcon loading={subDcaLoading} />
                                </div>
                            </div>
                            <div>
                                <Label required>PO Start Date</Label>
                                <CustomDatePicker value={startDate} onChange={(d) => { setStartDate(d); if (d && endDate && new Date(d) > new Date(endDate)) setEndDate(d); }} disabled={isBusy} maxDate={new Date()} />
                            </div>
                            <div>
                                <Label required>PO Completion Date</Label>
                                <CustomDatePicker value={endDate} onChange={setEndDate} disabled={isBusy} minDate={startDate || new Date()} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Section 2: Item Description ──────────────────────── */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <SectionHeader icon={Table2} title="Item Description" subtitle="PRW Rate acts as a ceiling — Rate cannot exceed it"
                        action={
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleDownloadTemplate}
                                    disabled={isBusy}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                                >
                                    <Download className="h-3.5 w-3.5" /> Template
                                </button>
                                <button
                                    onClick={() => excelInputRef.current?.click()}
                                    disabled={isBusy}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50"
                                >
                                    <Upload className="h-3.5 w-3.5" /> Upload Excel
                                </button>
                                <input
                                    ref={excelInputRef}
                                    type="file"
                                    accept=".xls,.xlsx"
                                    onChange={handleExcelInputChange}
                                    className="hidden"
                                />
                            </div>
                        }
                    />
                    <div className="p-6 md:p-8">
                        <p className="text-xs text-gray-400 mb-4">
                            For a lengthy item list, download the template, fill it in (columns: {EXCEL_COLUMNS.join(', ')}), and upload it — rows are validated and added to the table below.
                        </p>
                        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30">
                                        <th className="px-3 py-3 text-left text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Description</th>
                                        <th className="px-3 py-3 text-left text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Unit</th>
                                        <th className="px-3 py-3 text-right text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Qty</th>
                                        <th className="px-3 py-3 text-right text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Our Rate</th>
                                        <th className="px-3 py-3 text-right text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">PRW Rate</th>
                                        <th className="px-3 py-3 text-right text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Rate</th>
                                        <th className="px-3 py-3 text-right text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Amount</th>
                                        <th className="px-3 py-3 text-center text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((r, idx) => (
                                        <tr key={r.id} className={`border-t border-gray-100 dark:border-gray-700 ${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/50 dark:bg-gray-700/30'}`}>
                                            <td className="px-3 py-2">
                                                <textarea rows={1} className={`${inputCls} py-1.5`} value={r.description} onChange={(e) => updateItem(r.id, { description: e.target.value })} disabled={isBusy} />
                                            </td>
                                            <td className="px-3 py-2">
                                                <input type="text" className={`${inputCls} py-1.5`} value={r.unit} onChange={(e) => updateItem(r.id, { unit: e.target.value })} disabled={isBusy} />
                                            </td>
                                            <td className="px-3 py-2">
                                                <input type="text" inputMode="decimal" className={`${inputCls} py-1.5 text-right`} value={r.quantity} onChange={(e) => /^\d*\.?\d{0,2}$/.test(e.target.value) && updateItem(r.id, { quantity: e.target.value })} disabled={isBusy} />
                                            </td>
                                            <td className="px-3 py-2">
                                                <input type="text" inputMode="decimal" className={`${inputCls} py-1.5 text-right`} value={r.ourRate} onChange={(e) => /^\d*\.?\d{0,2}$/.test(e.target.value) && updateItem(r.id, { ourRate: e.target.value })} disabled={isBusy} />
                                            </td>
                                            <td className="px-3 py-2">
                                                <input type="text" inputMode="decimal" className={`${inputCls} py-1.5 text-right`} value={r.prwRate} onChange={(e) => /^\d*\.?\d{0,2}$/.test(e.target.value) && updateItem(r.id, { prwRate: e.target.value })} disabled={isBusy} />
                                            </td>
                                            <td className="px-3 py-2">
                                                <input type="text" inputMode="decimal" className={`${inputCls} py-1.5 text-right`} value={r.rate} onChange={(e) => /^\d*\.?\d{0,2}$/.test(e.target.value) && updateItem(r.id, { rate: e.target.value })} disabled={isBusy} />
                                            </td>
                                            <td className="px-3 py-2 text-right font-semibold text-gray-800 dark:text-gray-100">{formatIndianCurrency(itemAmount(r))}</td>
                                            <td className="px-3 py-2 text-center">
                                                <button onClick={() => setItems((p) => p.length > 1 ? p.filter((x) => x.id !== r.id) : p)} disabled={isBusy} className="p-1.5 rounded-lg text-rose-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 disabled:opacity-40">
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="border-t-2 border-indigo-200 dark:border-indigo-700 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30">
                                        <td colSpan={6} className="px-3 py-3 text-xs font-bold text-indigo-700 dark:text-indigo-300 uppercase tracking-wider">Total Amount</td>
                                        <td className="px-3 py-3 text-right text-base font-black text-indigo-700 dark:text-indigo-300">₹ {formatIndianCurrency(gridTotal)}</td>
                                        <td />
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                        {gridTotal > 0 && (
                            <p className="text-xs text-indigo-500 dark:text-indigo-400 mt-1.5 italic text-right">{getAmountDisplay(gridTotal).words}</p>
                        )}
                        <button onClick={() => setItems((p) => [...p, newItemRow()])} disabled={isBusy} className="mt-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 disabled:opacity-50">
                            <Plus className="h-3.5 w-3.5" /> Add New
                        </button>
                    </div>
                </div>

                {/* ── Section 3: Terms & Conditions ────────────────────── */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <SectionHeader icon={FileStack} title="Terms & Conditions" subtitle="Enter at least one term" />
                    <div className="p-6 md:p-8 space-y-3">
                        {terms.map((t, i) => (
                            <div key={t.id} className="flex items-center gap-3">
                                <span className="text-xs font-bold text-gray-400 w-6">{i + 1}.</span>
                                <textarea
                                    rows={1} className={`${inputCls} resize-none`}
                                    value={t.term}
                                    onChange={(e) => { if (!e.target.value.includes('|')) updateTerm(t.id, e.target.value); }}
                                    disabled={isBusy}
                                    placeholder="Enter term…"
                                />
                                <button onClick={() => setTerms((p) => p.length > 1 ? p.filter((x) => x.id !== t.id) : p)} disabled={isBusy} className="p-1.5 rounded-lg text-rose-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 disabled:opacity-40">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        ))}
                        <button onClick={() => setTerms((p) => [...p, newTermRow()])} disabled={isBusy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 disabled:opacity-50">
                            <Plus className="h-3.5 w-3.5" /> Add New Term
                        </button>
                    </div>
                </div>

                {/* ── Section 4: Remarks & Upload ───────────────────────── */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <SectionHeader icon={Paperclip} title="Remarks & Document" />
                    <div className="p-6 md:p-8">
                        <InnerHeader icon={FileStack} title="Remarks" />
                        <textarea rows={3} className={`${inputCls} resize-none mb-6`} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={isBusy} />

                        <Label required={requiresUpload}>Upload Final Scope Of Checklist (PDF){!requiresUpload && ' — optional'}</Label>
                        <input type="file" accept=".pdf,application/pdf" onChange={handleFileChange} disabled={isBusy} className={`${inputCls} py-2`} />
                        {file && <p className="text-xs text-gray-500 mt-1">{file.name} ({(file.size / 1024).toFixed(1)} KB)</p>}
                    </div>
                </div>

                {showSummary && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <SectionHeader icon={CheckCircle} title="PO Summary" subtitle="Review before submitting" />
                        <div className="p-6 md:p-8 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                            {[
                                { label: 'Vendor', value: vendors.find((v) => v.VendorCode === vendorCode)?.VendorName },
                                { label: 'Cost Center', value: ccCode },
                                { label: 'Start Date', value: formatDateForAPI(startDate) },
                                { label: 'Completion Date', value: formatDateForAPI(endDate) },
                                { label: 'No. of Items', value: items.length },
                                { label: 'Total Value', value: `₹ ${formatIndianCurrency(gridTotal)}` },
                                { label: 'Created By', value: userName },
                                { label: 'Remarks', value: remarks, wide: true },
                            ].map(({ label, value, wide }) => (
                                <div key={label} className={`bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 ${wide ? 'md:col-span-3' : ''}`}>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                                    <p className="font-semibold text-gray-800 dark:text-gray-100 break-words">{value ?? '—'}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-end gap-3 pb-6">
                    <button onClick={handleFullReset} disabled={isBusy} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50">
                        <RotateCcw className="h-4 w-4" /> Reset
                    </button>
                    <button onClick={handleSubmit} disabled={!canSubmit} className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                        {isBusy ? <><Loader2 className="h-4 w-4 animate-spin" /> {uploading ? 'Uploading…' : 'Saving…'}</> : <><Send className="h-4 w-4" /> Submit</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SPPOCreation;
